/* Whole-pool acquisition -> pinned identity deck -> fresh shared Liquid cards.
 * Storage contains identity history only. A failed or stale response never replaces native results.
 * No timers reshuffle a visible page. A traversal is carried in this tab's history/pagination.
 */
(function () {
  'use strict';
  var core = window.FJBCSoldRotation;
  if (!core) return;
  var CURRENT = 'filter.p.m.fjbc.catalogue_current', SOLD = 'filter.p.m.fjbc.sold_reference_live';
  var guard = document.querySelector('[data-sold-query-guard]');
  if (guard) {
    var normalized = core.normalizeURL(location.href, guard.dataset.param, guard.dataset.value);
    // One normalization only. If Shopify ignores it, the existing contact fallback remains usable.
    if (normalized.href !== location.href) location.replace(normalized.href);
    return;
  }
  var host = document.querySelector('[data-sold-discovery]');
  if (!host) return;
  // The parser-time guard already fell back. Never insert sold cards into that visible grid.
  if (host.dataset.soldLoaded === 'fallback') return;
  var mode = host.dataset.soldDiscovery, controller, exposureObserver, generation = 0, awayAt = null;
  var consentReady = false;
  function allowed() {
    try { return consentReady && window.Shopify.customerPrivacy.preferencesProcessingAllowed() === true; }
    catch (_) { return false; }
  }
  function clearHistories() {
    [localStorage, sessionStorage].forEach(function (area) {
      try {
        Object.keys(area).forEach(function (key) {
          if (key.indexOf('fjbc.sold.v1.') === 0) area.removeItem(key);
        });
      } catch (_) {}
    });
    try { sessionStorage.removeItem('fjbc.sold.pending'); } catch (_) {}
  }
  var url = new URL(location.href), pageNumber = Number(url.searchParams.get('page') || 1);
  if (!Number.isSafeInteger(pageNumber) || pageNumber < 1) return;
  if (mode === 'archive' && url.searchParams.get('sort_by')) return;
  if (mode === 'showcase' && (url.searchParams.get('sort_by') || url.searchParams.get('filter.v.availability') === '1')) return;
  // Variant-only constraints do not establish a same-variant match for an unavailable item.
  if (mode === 'showcase' && Array.from(url.searchParams.keys()).some(function (key) { return key.startsWith('filter.v.'); })) return;
  var context = new URL(url); context.searchParams.delete('page'); context.searchParams.delete('section_id');
  context.searchParams.delete('preview_theme_id');
  context.searchParams.sort();
  var storageKey = 'fjbc.sold.v1.' + mode + '.' + context.pathname + '?' + context.searchParams.toString();
  function read(key) { if (!allowed()) return null; try { return JSON.parse(sessionStorage.getItem(key)); } catch (_) { return null; } }
  function write(key, value) { if (!allowed()) return; try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* memory-only visit */ } }
  function readHistory() { if (!allowed()) return null; try { return JSON.parse(localStorage.getItem(storageKey)); } catch (_) { return null; } }
  function randomSeed() { var value = new Uint32Array(1); crypto.getRandomValues(value); return value[0]; }
  var state, initialGrid = host.querySelector('.sh-grid'), nativeHTML = initialGrid ? initialGrid.innerHTML : null;
  var nav = host.querySelector('[data-sold-pagination]'), nativeNav = nav ? nav.innerHTML : null;
  var historyKey = 'fjbcSoldTraversal';
  var continuing = false;
  function markTraversal() {
    continuing = history.state && history.state[historyKey] === storageKey;
    if (document.referrer) {
      var referrer = new URL(document.referrer);
      if (referrer.origin === location.origin && /\/products\//.test(referrer.pathname) && read(storageKey)) continuing = true;
    }
    try { history.replaceState(Object.assign({}, history.state, { [historyKey]: storageKey }), '', location.href); } catch (_) {}
  }
  async function acquire(signal) {
    var feed = new URL(location.href);
    feed.searchParams.delete(CURRENT); feed.searchParams.delete('sort_by');
    feed.searchParams.set(SOLD, host.dataset.soldValue || '1'); feed.searchParams.set('section_id', 'sold-pool');
    var cards = new Map(), identities = new Set(), expectedTotal, expectedPages;
    async function get(number) {
      var request = new URL(feed); request.searchParams.set('page', String(number));
      var response = await fetch(request, { signal: signal, credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Sold pool request failed');
      var html = await response.text();
      if (html.length > 4000000) throw new Error('Sold pool page exceeds budget');
      // Parsing inside a template, including its nested templates, is inert in the live document.
      var parsed = document.createElement('template'); parsed.innerHTML = html;
      var pool = parsed.content.querySelector('[data-sold-pool]');
      if (!pool || pool.dataset.version !== '1' || Number(pool.dataset.page) !== number) throw new Error('Invalid sold pool');
      var total = Number(pool.dataset.total), pages = Number(pool.dataset.pages);
      if (!Number.isSafeInteger(total) || total < 0 || total > 5000 || !Number.isSafeInteger(pages) || pages < 0 || (pages !== Math.ceil(total / 250) && !(total === 0 && pages === 1))) throw new Error('Unsupported sold pool size');
      if (expectedTotal === undefined) { expectedTotal = total; expectedPages = pages; }
      if (total !== expectedTotal || pages !== expectedPages) throw new Error('Sold pool changed during acquisition');
      var rows = pool.querySelectorAll('template[data-sold-card], [data-sold-ineligible]');
      if (rows.length !== Math.min(250, Math.max(0, total - (number - 1) * 250))) throw new Error('Incomplete sold page');
      rows.forEach(function (row) {
        var id = row.dataset.soldCard || row.dataset.soldIneligible;
        if (core.unique([id]).length !== 1 || identities.has(id)) throw new Error('Duplicate or invalid sold identity');
        identities.add(id);
        if (row.tagName === 'TEMPLATE' && (mode === 'archive' || row.dataset.showcase === 'true')) {
          if (row.content.querySelectorAll('.ptile-wrap').length !== 1) throw new Error('Invalid sold card');
          cards.set(id, row);
        }
      });
      await new Promise(function (resolve) { setTimeout(resolve, 0); });
    }
    await get(1);
    var next = 2;
    await Promise.all([0, 1, 2].map(async function () { while (next <= expectedPages) { var number = next++; await get(number); } }));
    if (identities.size !== expectedTotal) throw new Error('Incomplete sold pool');
    // Request completion order is irrelevant to seeded ordering.
    return new Map(Array.from(cards.entries()).sort(function (a, b) { return a[0].localeCompare(b[0]); }));
  }
  function restoreNative() {
    if (initialGrid && nativeHTML !== null) initialGrid.innerHTML = nativeHTML;
    if (nav && nativeNav !== null) nav.innerHTML = nativeNav;
  }
  function render(cards) {
    if (!allowed()) return;
    if (!initialGrid && mode === 'archive') {
      initialGrid = document.createElement('div'); initialGrid.className = 'sh-grid'; nativeHTML = '';
      host.prepend(initialGrid);
    }
    if (!initialGrid) return;
    var live = Array.from(cards.keys()), saved = continuing ? read(storageKey) : readHistory(), now = Date.now();
    if (!state) state = core.start(live, saved && saved.deck, { now: now, seed: randomSeed(), freshEntry: !continuing,
      awaySince: saved && saved.awayAt });
    var chosen = core.page(state.order, live, pageNumber, mode === 'archive' ? 48 : 2), shown = [];
    restoreNative();
    if (mode === 'archive') {
      initialGrid.replaceChildren();
      chosen.forEach(function (id) { initialGrid.appendChild(document.importNode(cards.get(id).content, true)); shown.push(id); });
      if (!chosen.length) { var empty = document.createElement('p'); empty.className = 'sh-empty'; empty.textContent = 'No items on this page.'; initialGrid.appendChild(empty); }
      if (nav) {
        nav.replaceChildren();
        var pages = Math.ceil(state.order.length / 48);
        [pageNumber - 1, pageNumber + 1].forEach(function (number) {
          if (number < 1 || number > pages) return;
          var link = document.createElement('a'), target = new URL(location.href); target.searchParams.set('page', String(number));
          link.href = target.href; link.textContent = number < pageNumber ? 'Previous' : 'Next'; nav.appendChild(link);
        });
      }
    } else {
      var current = Array.from(initialGrid.children).filter(function (card) { return card.matches('.ptile-wrap'); });
      if (current.length > 46) return;
      chosen.forEach(function (id, index) {
        var anchor = current[index === 0 ? 22 : 45];
        if (anchor) { anchor.after(document.importNode(cards.get(id).content, true)); shown.push(id); }
      });
    }
    // Count exposure when a selected card reaches the viewport, not when a template is downloaded.
    if ('IntersectionObserver' in window) {
      if (exposureObserver) exposureObserver.disconnect();
      exposureObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || !allowed() || !state) return;
          state = core.expose(state, [entry.target.dataset.handle]); persist(); exposureObserver.unobserve(entry.target);
        });
      });
      initialGrid.querySelectorAll('.pcard--sold[data-handle]').forEach(function (card) { if (shown.includes(card.dataset.handle)) exposureObserver.observe(card); });
    } else { state = core.expose(state, shown); }
    persist(); host.dataset.soldLoaded = 'true';
  }
  function persist() {
    if (!state || !allowed()) return;
    var value = { deck: state, awayAt: awayAt };
    write(storageKey, value);
    try { localStorage.setItem(storageKey, JSON.stringify(value)); } catch (_) { /* tab-local fallback */ }
  }
  async function load() {
    if (!allowed()) return;
    var run = ++generation; if (controller) controller.abort(); controller = new AbortController();
    host.dataset.soldLoading = 'active'; host.setAttribute('aria-busy', 'true');
    var active = controller, timeout;
    // Race the deadline as well as aborting: even a late/uncooperative response cannot repaint
    // the fallback after it becomes visible.
    var deadline = new Promise(function (_, reject) {
      timeout = setTimeout(function () { active.abort(); reject(new Error('Sold pool timed out')); }, 5000);
    });
    try { var cards = await Promise.race([acquire(active.signal), deadline]); if (run === generation && !active.signal.aborted && allowed()) render(cards); }
    catch (_) { if (run === generation) { restoreNative(); host.dataset.soldLoaded = 'fallback'; } }
    finally {
      clearTimeout(timeout);
      if (run === generation) { delete host.dataset.soldLoading; host.removeAttribute('aria-busy'); }
    }
  }
  function leave() {
    if (!allowed()) return;
    // Hide before removing stale sold cards, including browser Back and tab restoration.
    host.dataset.soldLoading = 'paused'; host.setAttribute('aria-busy', 'true');
    awayAt = Date.now(); persist(); generation++; if (controller) controller.abort();
    if (mode === 'archive') {
      // Never expose a cached sold page on Back before fresh eligibility comes back.
      if (initialGrid) initialGrid.replaceChildren(); if (nav) nav.replaceChildren();
    } else if (initialGrid) { initialGrid.querySelectorAll('.pcard--sold').forEach(function (card) { card.remove(); }); }
  }
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) leave();
    else syncConsent();
  });
  window.addEventListener('pagehide', leave);
  window.addEventListener('pageshow', function (event) { if (event.persisted) syncConsent(); });
  function syncConsent(clearDenied) {
    if (allowed()) {
      if (document.hidden) return;
      markTraversal(); awayAt = null; load();
    } else {
      generation++; if (controller) controller.abort();
      if (exposureObserver) exposureObserver.disconnect();
      state = null; awayAt = null; continuing = false;
      if (consentReady || clearDenied === true) clearHistories();
      restoreNative();
      delete host.dataset.soldLoading; host.removeAttribute('aria-busy');
      host.dataset.soldLoaded = 'fallback';
      try {
        if (history.state && history.state[historyKey]) {
          var nextState = Object.assign({}, history.state); delete nextState[historyKey];
          history.replaceState(nextState, '', location.href);
        }
      } catch (_) {}
    }
  }
  document.addEventListener('visitorConsentCollected', function () { syncConsent(true); });
  syncConsent();
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      consentReady = !error; syncConsent(true);
    });
  }
})();
